<template>
  <modal
    :elem-id="modalId"
    :title="modalTitle"
    :close-label="closeLabel"
    :save-label="saveLabel"
    :save-fn="handleSave"
    :show="isModalVisible"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isModalVisible">
      <occurrences-form
        ref="occurrencesFormRef"
        :owner="owner"
        :owner-id="ownerId"
        :save-label="saveLabel"
        :elem-id="formElemId"
        :show-save-btn="false">
      </occurrences-form>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import OccurrencesForm from './OccurrencesForm.vue';

type OwnerId = string | number | null;

interface OccurrencesFormInstance {
  salvaNovaOcorrencia: () => Promise<void>;
  resetForm: () => void;
}

interface ModalOpenPayload {
  owner?: string;
  ownerId?: OwnerId;
  elemId?: string;
}

const props = withDefaults(defineProps<{
  owner?: string;
  ownerId?: OwnerId;
  modalTitle?: string;
  descriptionLabel?: string;
  closeLabel?: string;
  saveLabel?: string;
}>(), {
  owner: '',
  ownerId: null,
  modalTitle: 'Nova Ocorrência',
  descriptionLabel: 'Descrição',
  closeLabel: 'Fechar',
  saveLabel: 'Salvar'
});

const isModalVisible = ref<boolean>(false);
const modalId = ref<string>('ocorrencia-modal');
const occurrencesFormRef = ref<OccurrencesFormInstance | null>(null);
const currentOwner = ref<string>('');
const currentOwnerId = ref<OwnerId>(null);
const formElemId = ref<string>('inputNovaOcorrencia');

const owner = computed<string>(() => currentOwner.value !== '' ? currentOwner.value : props.owner);
const ownerId = computed<OwnerId>(() => currentOwnerId.value ?? props.ownerId);

const handleModalOpen = (data: ModalOpenPayload | undefined): void => {
  log('OccurrencesModal', 'handleModalOpen', 'Modal open event received', data);
  if (data && Object.prototype.hasOwnProperty.call(data, 'owner')) {
    currentOwner.value = data.owner ?? '';
  }
  if (data && Object.prototype.hasOwnProperty.call(data, 'ownerId')) {
    currentOwnerId.value = data.ownerId ?? null;
  }
  if (data?.elemId) {
    formElemId.value = data.elemId;
  }
  openModal();
};

const openModal = (): void => {
  isModalVisible.value = true;
  log('OccurrencesModal', 'openModal', 'Modal opened');
};

const closeModal = (): void => {
  isModalVisible.value = false;
  if (occurrencesFormRef.value) {
    occurrencesFormRef.value.resetForm();
  }
  currentOwner.value = '';
  currentOwnerId.value = null;
  formElemId.value = 'inputNovaOcorrencia';
  log('OccurrencesModal', 'closeModal', 'Modal closed');
};

const handleSave = (): void => {
  if (occurrencesFormRef.value) {
    occurrencesFormRef.value.salvaNovaOcorrencia();
  }
};

const handleSaveSuccess = (): void => {
  if (!isModalVisible.value) {
    return;
  }
  closeModal();
  eventBus.emit('occurrences.saved');
  log('OccurrencesModal', 'handleSaveSuccess', 'Save successful, emitted occurrences.saved');
};

onMounted(() => {
  log('OccurrencesModal', 'onMounted', 'Component mounted');
  eventBus.on('modal.occurrences.open', handleModalOpen);
  eventBus.on('modal.occurrences.close', closeModal);
  eventBus.on('ocorrencia.saved', handleSaveSuccess);
});

onBeforeUnmount(() => {
  log('OccurrencesModal', 'onBeforeUnmount', 'Component unmounting');
  eventBus.off('modal.occurrences.open', handleModalOpen);
  eventBus.off('modal.occurrences.close', closeModal);
  eventBus.off('ocorrencia.saved', handleSaveSuccess);
});
</script>

<style scoped>
/* Modal-specific styles - centralized modal pattern */
</style>
